import { CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS } from "@tablescape/constants";
import {
  type CartProduct,
  type CartProductVariant,
  type GwpOffer,
  type PickYourOwnAddPreview,
  type SpendingGwpTierConfig,
} from "@tablescape/state";

import { type CartLineLike, getGiftWithPurchaseAttributeValue } from "./cart";
import { getLoyaltyTier } from "./customer";
import {
  getCartSpendingAmountExcludingGwpGifts,
  getEligibleSpendingGwpTierConfigs,
  getProductGwpOffersFromCartLines,
  isGwpOfferPickYourOwnPromptEligible,
  mapSpendingGwpTierConfigToGwpOffer,
} from "./giftWithPurchaseOffers";
import { gwpInteractionKeyForOffer } from "./gwpInteractionStore";
import { isProductVariantAvailableForSale } from "./product";
import { isProductGwpGiftLine } from "./productGwpCartLines";

const isMultiGiftPickYourOwnOffer = (offer: GwpOffer) =>
  offer.giftProductIds.length > 1;

export type GiftDrawerOpen = {
  offer: GwpOffer;
  productName: string;
};

const eligiblePickYourOwnOffers = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  spendingGwpTierConfigs: SpendingGwpTierConfig[] | undefined,
  options?: {
    buttonText?: string;
    excludedFromTieredProgressBarProductIds?: readonly string[];
    loyaltyTier?: string | null;
  }
): GwpOffer[] => {
  const loyaltyTier =
    options?.loyaltyTier !== undefined ? options.loyaltyTier : getLoyaltyTier();

  return [
    ...getEligibleSpendingGwpTierConfigs(
      spendingGwpTierConfigs,
      getCartSpendingAmountExcludingGwpGifts(
        lines,
        options?.excludedFromTieredProgressBarProductIds
      ),
      { loyaltyTier }
    ).map(mapSpendingGwpTierConfigToGwpOffer),
    ...getProductGwpOffersFromCartLines(lines, options),
  ].filter((offer) => isGwpOfferPickYourOwnPromptEligible(offer, loyaltyTier));
};

const lineProductName = (line: CartLineLike | null | undefined): string => {
  const productTitle = line?.merchandise?.product?.title?.trim();
  if (productTitle) {
    return productTitle;
  }

  const variantTitle = line?.merchandise?.title?.trim();
  if (variantTitle && variantTitle !== "Default Title") {
    return variantTitle;
  }

  return "";
};

const isGiftCartLine = (line: CartLineLike | null | undefined): boolean =>
  !!line &&
  (isProductGwpGiftLine(line) ||
    !!getGiftWithPurchaseAttributeValue(
      line,
      CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.SPENDING_THRESHOLD
    ));

const addedProductName = (
  previousLines: Array<CartLineLike | null | undefined> | null | undefined,
  nextLines: Array<CartLineLike | null | undefined> | null | undefined,
  offer: GwpOffer
): string => {
  if (offer.kind === "product") {
    return lineProductName(
      nextLines?.find((line) => line?.id === offer.trigger.cartLineId)
    );
  }

  const previousLineFor = (id: string) =>
    previousLines?.find((line) => line?.id === id);
  const addedLine = (nextLines ?? []).find(
    (line) => line?.id && !previousLineFor(line.id) && !isGiftCartLine(line)
  );
  if (addedLine) {
    return lineProductName(addedLine);
  }

  const changedLine = (nextLines ?? []).find((line) => {
    const previousLine = line?.id ? previousLineFor(line.id) : undefined;

    return (
      !!line?.id &&
      !!previousLine &&
      !isGiftCartLine(line) &&
      (previousLine.cost?.totalAmount?.amount ?? "") !==
        (line.cost?.totalAmount?.amount ?? "")
    );
  });

  return lineProductName(changedLine);
};

export const pickYourOwnAddPreview = (
  variants:
    | Array<
        Pick<
          CartProductVariant,
          "giftWithPurchase" | "id" | "product" | "selectedOptions"
        >
      >
    | undefined
): PickYourOwnAddPreview | undefined => {
  const primary = variants?.length === 1 ? variants[0] : undefined;
  if (!primary) {
    return undefined;
  }

  return {
    giftWithPurchase: primary.giftWithPurchase ?? null,
    parentSelectedOptions: primary.selectedOptions,
    parentVariantId: primary.id,
    productName: primary.product?.title ?? "",
  };
};

export const findGiftDrawerToOpenOnClick = ({
  loyaltyTier,
  preview,
}: {
  loyaltyTier?: string | null;
  preview: PickYourOwnAddPreview;
}): {
  drawer: GiftDrawerOpen;
  giftProducts: CartProduct[];
} | null => {
  const gift = preview.giftWithPurchase;
  const listsSeveralGifts = (gift?.products?.length ?? 0) > 1;
  const giftProducts = (gift?.products ?? []).filter((product) =>
    (product.eligibleGiftVariants ?? product.variants).some(
      isProductVariantAvailableForSale
    )
  );
  if (
    !gift?.title ||
    gift.autoAddProducts?.length ||
    !listsSeveralGifts ||
    !giftProducts.length
  ) {
    return null;
  }

  const offer: GwpOffer = {
    autoAddProductIds: [],
    buttonText: "",
    eligibleLoyaltyTiers: gift.eligibleLoyaltyTiers ?? null,
    giftItemBadgeText: gift.giftBadgeText ?? "",
    giftProductIds: giftProducts.map((product) => product.id),
    id: preview.parentVariantId,
    kind: "product",
    title: gift.title,
    trigger: {
      type: "parentLine",
      // The cart has no line yet, so the drawer waits for the parent line.
      cartLineId: "",
      parentVariantId: preview.parentVariantId,
    },
  };

  return isGwpOfferPickYourOwnPromptEligible(offer, loyaltyTier)
    ? { drawer: { offer, productName: preview.productName }, giftProducts }
    : null;
};

const parentVariantQuantity = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentVariantId: string
) =>
  (lines ?? []).reduce((total, line) => {
    if (
      !line ||
      line.merchandise?.id !== parentVariantId ||
      isGiftCartLine(line)
    ) {
      return total;
    }

    const quantity = line.quantity;

    return (
      total + (typeof quantity === "number" && quantity > 0 ? quantity : 1)
    );
  }, 0);

const findRepeatParentOffer = (
  offers: GwpOffer[],
  previousLines: Array<CartLineLike | null | undefined> | null | undefined,
  nextLines: Array<CartLineLike | null | undefined> | null | undefined
): GwpOffer | undefined =>
  offers.find((offer) => {
    if (offer.kind !== "product" || !isMultiGiftPickYourOwnOffer(offer)) {
      return false;
    }

    const previousQuantity = parentVariantQuantity(
      previousLines,
      offer.trigger.parentVariantId
    );
    const nextQuantity = parentVariantQuantity(
      nextLines,
      offer.trigger.parentVariantId
    );

    return previousQuantity > 0 && nextQuantity > previousQuantity;
  });

const offerToOpen = (
  newlyQualified: GwpOffer[],
  repeatParent: GwpOffer | undefined
): GwpOffer | undefined =>
  newlyQualified.find((offer) => offer.kind === "product") ??
  repeatParent ??
  newlyQualified.find((offer) => offer.kind === "spending");

export const findGiftDrawerToOpenAfterCartChange = ({
  buttonText,
  excludedFromTieredProgressBarProductIds,
  loyaltyTier,
  nextLines,
  previousLines,
  spendingGwpTierConfigs,
}: {
  buttonText?: string;
  excludedFromTieredProgressBarProductIds?: readonly string[];
  loyaltyTier?: string | null;
  nextLines: Array<CartLineLike | null | undefined> | null | undefined;
  previousLines: Array<CartLineLike | null | undefined> | null | undefined;
  spendingGwpTierConfigs: SpendingGwpTierConfig[] | undefined;
}): GiftDrawerOpen | null => {
  const options = {
    buttonText,
    excludedFromTieredProgressBarProductIds,
    loyaltyTier,
  };
  const previousKeys = new Set(
    eligiblePickYourOwnOffers(
      previousLines,
      spendingGwpTierConfigs,
      options
    ).map(gwpInteractionKeyForOffer)
  );
  const nextOffers = eligiblePickYourOwnOffers(
    nextLines,
    spendingGwpTierConfigs,
    options
  );
  const newlyQualified = nextOffers.filter(
    (offer) =>
      isMultiGiftPickYourOwnOffer(offer) &&
      !previousKeys.has(gwpInteractionKeyForOffer(offer))
  );
  const offer = offerToOpen(
    newlyQualified,
    findRepeatParentOffer(nextOffers, previousLines, nextLines)
  );

  if (!offer) {
    return null;
  }

  return {
    offer,
    productName: addedProductName(previousLines, nextLines, offer),
  };
};
